import React, { useMemo, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { toast } from 'sonner';
import { DropColumn, DragCard } from './KanbanDnd';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Clock, Calendar, DollarSign, Percent, FileText,
  ChevronRight, X, MessageSquare, User, Sparkles
} from 'lucide-react';

const UF_TO_CLP = 40000;

// Columnas del Kanban según los estados actuales del negocio
const KANBAN_COLUMNS = [
  { key: 'Borrador', label: 'Propuesta', color: 'bg-slate-500', chipColor: 'bg-slate-100 text-slate-700 border-slate-300' },
  { key: 'En Negociación', label: 'En Negociación', color: 'bg-amber-500', chipColor: 'bg-amber-100 text-amber-800 border-amber-300' },
  { key: 'Enviada', label: 'Enviada', color: 'bg-blue-500', chipColor: 'bg-blue-100 text-blue-800 border-blue-300' },
  { key: 'Primer Pago', label: 'Primer Pago', color: 'bg-orange-500', chipColor: 'bg-orange-100 text-orange-800 border-orange-300' },
  { key: 'Ejecución/Activa', label: 'Aceptada', color: 'bg-emerald-500', chipColor: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { key: 'Rechazada', label: 'Rechazada', color: 'bg-red-500', chipColor: 'bg-red-100 text-red-800 border-red-300' },
];

const KanbanVentas = ({ proposals = [], clients = [], onOpenProposal, renderActions, canMove, moveBlockReason, movesFrom, onMove }) => {
  const [selectedId, setSelectedId] = useState(null);
  const selected = proposals.find(p => p.id === selectedId) || null;
  const setSelected = (p) => setSelectedId(p?.id || null);

  // Agrupar por status
  const grouped = useMemo(() => {
    const map = {};
    KANBAN_COLUMNS.forEach(c => { map[c.key] = []; });
    proposals.forEach(p => {
      const s = p.status === 'Aceptada' ? 'Ejecución/Activa' : (p.status || 'Borrador');
      if (!map[s]) map[s] = [];
      map[s].push(p);
    });
    return map;
  }, [proposals]);

  const getClient = (id) => clients.find(c => c.id === id);

  const handleCardClick = (proposal) => {
    setSelected(proposal);
    if (onOpenProposal) onOpenProposal(proposal);
  };

  const formatUF = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
  };

  const formatCLP = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('es-CL', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const colKeyOf = (p) => (p.status === 'Aceptada' ? 'Ejecución/Activa' : (p.status || 'Borrador'));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [draggingId, setDraggingId] = useState(null);
  const dragging = proposals.find(p => p.id === draggingId) || null;
  const dropStateFor = (key) => {
    if (!dragging || key === colKeyOf(dragging)) return null;
    return canMove?.(dragging, key) ? 'allowed' : 'blocked';
  };
  const handleDragEnd = ({ active, over }) => {
    setDraggingId(null);
    const p = proposals.find(x => x.id === active.id);
    if (!p || !over || over.id === colKeyOf(p)) return;
    if (!canMove?.(p, over.id)) {
      const label = KANBAN_COLUMNS.find(c => c.key === over.id)?.label || over.id;
      const from = KANBAN_COLUMNS.find(c => c.key === colKeyOf(p))?.label || p.status;
      toast.error(`No se puede mover ${p.folio || 'la PSP'} de "${from}" a "${label}"`, {
        description: <span style={{ color: '#7f1d1d', opacity: 1 }} data-testid="kanban-move-error-reason">{moveBlockReason?.(p, over.id)}</span>,
        duration: 6000,
        className: 'kanban-move-error',
        style: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' },
      });
      return;
    }
    onMove?.(p, over.id);
  };

  return (
    <DndContext sensors={sensors} onDragStart={({ active }) => setDraggingId(active.id)} onDragEnd={handleDragEnd} onDragCancel={() => setDraggingId(null)}>
    <div className="flex gap-4 w-full" data-testid="kanban-ventas-container">
      {/* ===== KANBAN BOARD (izquierda) ===== */}
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-4">
          {KANBAN_COLUMNS.map(col => {
            const items = grouped[col.key] || [];
            return (
              <DropColumn key={col.key} id={col.key} dropState={dropStateFor(col.key)} className="flex flex-col w-80 bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${col.color}`} />
                    <h3 className="font-semibold text-slate-800 text-sm">{col.label}</h3>
                    <Badge className="bg-white text-slate-600 border border-slate-300 text-xs">
                      {items.length}
                    </Badge>
                  </div>
                </div>

                <div className="flex flex-col gap-2.5 overflow-y-auto max-h-[calc(100vh-260px)]">
                  {items.length === 0 ? (
                    <div className="text-xs text-slate-500 text-center py-6 border border-dashed border-slate-300 rounded">
                      Sin propuestas
                    </div>
                  ) : (
                    items.map(p => {
                      const client = getClient(p.client_id);
                      const precioUF = p.precio_uf || (p.precio_total ? p.precio_total / UF_TO_CLP : null);
                      const numCuotas = p.cuotas?.length || p.num_cuotas || 0;
                      const meses = p.meses_ejecucion || 0;
                      const esFeeResultados = !!(p.honorario_resultado_texto && String(p.honorario_resultado_texto).trim().length > 0);
                      const negocio = p.titulo || p.folio || 'Sin título';

                      return (
                        <DragCard
                          key={p.id}
                          id={p.id}
                          disabled={!movesFrom?.(p)}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleCardClick(p)}
                          data-testid={`kanban-card-${p.id}`}
                          className={`text-left cursor-pointer bg-white rounded-lg border-2 p-3 transition-[box-shadow,border-color,opacity] hover:shadow-md hover:border-orange-400 ${
                            selected?.id === p.id ? 'border-orange-500 shadow-md' : 'border-slate-200'
                          }`}
                        >
                          {/* Folio + Fee resultados */}
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-mono text-[10px] text-slate-500 uppercase tracking-wide">
                              {p.folio || '—'}
                            </span>
                            <div className="flex items-center gap-1">
                              {esFeeResultados && (
                                <Badge className="bg-violet-100 text-violet-700 border-violet-300 text-[10px] px-1.5 py-0" data-testid={`fee-resultados-tag-${p.id}`}>
                                  <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                                  Fee a resultados
                                </Badge>
                              )}
                              {renderActions?.(p)}
                            </div>
                          </div>

                          {/* Nombre del negocio */}
                          <h4 className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2 leading-tight">
                            {negocio}
                          </h4>

                          {/* Cliente */}
                          <p className="text-xs text-slate-600 mb-2.5 truncate">
                            {client?.name || p.client_name || p.prospecto_snapshot?.nombre || 'Cliente no identificado'}
                          </p>

                          {/* Métricas */}
                          <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                            <div className="flex items-center gap-1 text-slate-600">
                              <Clock className="w-3 h-3 text-slate-400" />
                              <span className="font-medium">{meses} {meses === 1 ? 'mes' : 'meses'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <DollarSign className="w-3 h-3 text-slate-400" />
                              <span className="font-medium">{precioUF ? `${formatUF(precioUF)} UF` : '—'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              <span className="font-medium">{numCuotas} cuota{numCuotas === 1 ? '' : 's'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Percent className="w-3 h-3 text-slate-400" />
                              <span className="font-medium">{p.categoria_servicio || p.tipo_servicio || '—'}</span>
                            </div>
                          </div>
                        </DragCard>
                      );
                    })
                  )}
                </div>
              </DropColumn>
            );
          })}
        </div>
      </div>

      {/* ===== PANEL LATERAL DERECHO (detalle) ===== */}
      {selected && (
        <div className="w-[420px] flex-shrink-0 bg-white border border-slate-200 rounded-lg shadow-lg flex flex-col max-h-[calc(100vh-160px)]" data-testid="kanban-detail-panel">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-start justify-between">
              <div className="flex-1 pr-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-slate-500 uppercase">{selected.folio || '—'}</span>
                  <Badge className={`text-[10px] px-1.5 py-0 border ${
                    selected.status === 'Borrador' ? 'bg-slate-100 text-slate-700 border-slate-300' :
                    selected.status === 'En Negociación' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                    selected.status === 'Rechazada' ? 'bg-red-100 text-red-800 border-red-300' :
                    ['Enviada', 'Primer Pago'].includes(selected.status) ? 'bg-blue-100 text-blue-800 border-blue-300' :
                    'bg-emerald-100 text-emerald-800 border-emerald-300'
                  }`}>
                    {selected.status}
                  </Badge>
                </div>
                <h3 className="text-base font-bold text-slate-900 leading-snug">{selected.titulo || 'Sin título'}</h3>
                <p className="text-xs text-slate-600 mt-1">
                  {getClient(selected.client_id)?.name || selected.client_name || 'Cliente no identificado'}
                </p>
              </div>
              {renderActions?.(selected)}
              <button
                onClick={() => setSelected(null)}
                data-testid="kanban-detail-close"
                className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Contenido scrolleable */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Métricas clave */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-50 rounded p-2.5 border border-slate-200">
                <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Valor</div>
                <div className="text-sm font-bold text-slate-900">
                  {selected.precio_uf ? `${formatUF(selected.precio_uf)} UF` : formatCLP(selected.precio_total)}
                </div>
                {selected.precio_uf && selected.precio_total && (
                  <div className="text-[10px] text-slate-500">{formatCLP(selected.precio_total)}</div>
                )}
              </div>
              <div className="bg-slate-50 rounded p-2.5 border border-slate-200">
                <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Duración</div>
                <div className="text-sm font-bold text-slate-900">
                  {selected.meses_ejecucion || '—'} {selected.meses_ejecucion === 1 ? 'mes' : 'meses'}
                </div>
              </div>
              <div className="bg-slate-50 rounded p-2.5 border border-slate-200">
                <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Cuotas</div>
                <div className="text-sm font-bold text-slate-900">
                  {selected.cuotas?.length || selected.num_cuotas || '—'}
                </div>
              </div>
              <div className="bg-slate-50 rounded p-2.5 border border-slate-200">
                <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Categoría</div>
                <div className="text-sm font-bold text-slate-900">
                  {selected.categoria_servicio || selected.tipo_servicio || '—'}
                </div>
              </div>
            </div>

            {/* Fee a resultados */}
            {selected.honorario_resultado_texto && (
              <div className="bg-violet-50 border border-violet-200 rounded p-3" data-testid="detail-fee-resultados">
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                  <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Fee a resultados</span>
                </div>
                <p className="text-xs text-violet-800">{selected.honorario_resultado_texto}</p>
              </div>
            )}

            {/* Descripción */}
            {selected.descripcion && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Descripción</h4>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{selected.descripcion}</p>
              </div>
            )}

            {/* Cuotas (si existen) */}
            {selected.cuotas && selected.cuotas.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Cuotas ({selected.cuotas.length})</h4>
                <div className="space-y-1.5">
                  {selected.cuotas.map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2.5 py-1.5 border border-slate-200">
                      <span className="text-slate-600 font-medium">Cuota #{c.numero || idx + 1}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 font-semibold">{formatCLP(c.monto)}</span>
                        <Badge className={`text-[9px] px-1.5 py-0 border ${
                          c.status === 'Facturada' || c.status === 'Pagada'
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                            : c.status === 'Vencida'
                            ? 'bg-red-100 text-red-700 border-red-300'
                            : 'bg-slate-100 text-slate-600 border-slate-300'
                        }`}>
                          {c.status || 'Pendiente'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bitácora comercial */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Bitácora</h4>
                <Badge className="bg-slate-100 text-slate-600 border border-slate-300 text-[9px] px-1.5 py-0">
                  {(selected.bitacora_comercial || []).length}
                </Badge>
              </div>
              {(selected.bitacora_comercial || []).length === 0 ? (
                <p className="text-xs text-slate-500 italic bg-slate-50 rounded p-2 border border-slate-200">
                  Sin entradas de bitácora todavía.
                </p>
              ) : (
                <div className="space-y-2">
                  {(selected.bitacora_comercial || []).slice().reverse().map((entry, idx) => (
                    <div key={idx} className="bg-slate-50 rounded p-2.5 border border-slate-200 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={`text-[9px] px-1.5 py-0 border ${
                          entry.tipo === 'Generación PDF' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                          entry.tipo === 'Aceptación' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                          entry.tipo === 'Actualización' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                          'bg-slate-100 text-slate-600 border-slate-300'
                        }`}>
                          {entry.tipo || 'Nota'}
                        </Badge>
                        <span className="text-[10px] text-slate-500">{formatDate(entry.fecha)}</span>
                      </div>
                      {entry.usuario && (
                        <div className="flex items-center gap-1 text-slate-500 text-[10px] mb-0.5">
                          <User className="w-2.5 h-2.5" />
                          {entry.usuario}
                        </div>
                      )}
                      <p className="text-slate-700 leading-snug">{entry.descripcion || '—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fechas */}
            <div className="border-t border-slate-200 pt-3 space-y-1.5 text-xs">
              <div className="flex items-center justify-between text-slate-600">
                <span>Creada:</span>
                <span className="font-medium">{formatDate(selected.created_at)}</span>
              </div>
              {selected.fecha_aceptacion && (
                <div className="flex items-center justify-between text-slate-600">
                  <span>Aceptada:</span>
                  <span className="font-medium text-emerald-700">{formatDate(selected.fecha_aceptacion)}</span>
                </div>
              )}
              {selected.updated_at && (
                <div className="flex items-center justify-between text-slate-600">
                  <span>Última actualización:</span>
                  <span className="font-medium">{formatDate(selected.updated_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    <DragOverlay>
      {dragging && (
        <div className="w-80 bg-white rounded-lg border-2 border-orange-500 p-3 shadow-xl rotate-2 cursor-grabbing" data-testid="kanban-drag-overlay">
          <span className="font-mono text-[10px] text-slate-500 uppercase">{dragging.folio}</span>
          <h4 className="font-semibold text-slate-900 text-sm line-clamp-2">{dragging.titulo || 'Sin título'}</h4>
        </div>
      )}
    </DragOverlay>
    </DndContext>
  );
};

export default KanbanVentas;
